import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import { cn } from 'helpers/classnames';
import { evSaveChangesClick, evSubscriptionsPageChange, evUnsubscribeFromAllClick, pvSubscriptionsPageView } from 'events/account';
import { titaniteView, track } from 'apis/amethyst';
import useEffectOnce from 'hooks/useEffectOnce';
import { checkCustomerAuthentication } from 'actions/authentication';
import { redirectTo, redirectToAuthenticationFor } from 'actions/redirect';
import {
  fetchSubscriptionsInfo,
  fetchSubscriptionsSignupCaptcha,
  REQUEST_TYPE_MYACCOUNT_AUTH,
  REQUEST_TYPE_MYACCOUNT_TOKEN,
  REQUEST_TYPE_SIGNUP,
  REQUEST_TYPE_SUCCESS_MESSAGE,
  REQUEST_TYPE_VILLAGEIDIOT,
  signupForMailingList,
  toggleSubscriptionBrand,
  toggleSubscriptionList,
  toggleSubscriptionStock,
  toggleSubscriptionUpcomingStyle,
  updateSubscriptions
} from 'actions/account/subscriptions';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import EmailListSubscriptionListItem from 'components/account/Subscriptions/EmailListSubscriptionListItem';
import BrandSubscriptionListItem from 'components/account/Subscriptions/BrandSubscriptionListItem';
import ProductSubscriptionListItem from 'components/account/Subscriptions/ProductSubscriptionListItem';
import AccountHeader from 'containers/account/AccountHeader';
import useMartyContext from 'hooks/useMartyContext';
import { HYDRA_LAUNCH_CALENDAR } from 'constants/hydraTests';
import { isAssigned, triggerAssignment } from 'actions/ab';

import css from 'styles/containers/account/subscriptions.scss';

/**
 * This block helps to determine what this subscription view is supposed to do.
 * Because the subscriptions controller is taking over legacy functionality from myaccount and we need to
 * maintain backward compatibility with the existing url structure, there is some logic baked into the path.
 *
 * It's also important to note that data is mastered differently for different sites - village idiot or my account are sources of truth.
 *
 * @param {*} requestSpecs - the url params
 * @param {*} myAccountIsSubscriptionMaster - the configuration that says what the subscription source of truth is for this site
 */
export const determineRequestType = (requestSpecs = {}, myAccountIsSubscriptionMaster = false) => {
  if (requestSpecs.tokenTypeOrAction && ['o', 'u'].includes(requestSpecs.tokenTypeOrAction) && requestSpecs.token) {
    // make api calls to my account with the token in the path
    return REQUEST_TYPE_MYACCOUNT_TOKEN;
  } else if (requestSpecs.tokenTypeOrAction && requestSpecs.tokenTypeOrAction === 'signup') {
    // TODO determine if this is a logged in or not, implement functionality
    return REQUEST_TYPE_SIGNUP;
  } else if (requestSpecs.tokenTypeOrAction && ['signupSuccess', 'success'].includes(requestSpecs.tokenTypeOrAction)) {
    // These are legacy myaccount urls that just display success messages -- we can safely redirect to the top-level subs page
    return REQUEST_TYPE_SUCCESS_MESSAGE;
  } else if (myAccountIsSubscriptionMaster) {
    // make api calls to my account with the logged-in user's auth
    return REQUEST_TYPE_MYACCOUNT_AUTH;
  } else {
    // make api calls to village idiot with the logged-in user's auth or the token in the query params
    return REQUEST_TYPE_VILLAGEIDIOT;
  }
};

const makeAccountHeader = () => (
  <div className={css.accountHeader}>
    <AccountHeader title="Email Subscription Preferences" showCustomerEmail={false} />
  </div>
);

const makeSuccessBanner = message => <div className={css.successMessage}>{message}</div>;

const makeSignupForm = (captcha, onSignup, { siteName, privacyNotice, privacyNoticeText, errors = [] }) => (
  <div className={css.captcha}>
    <div className={css.accountHeader}>
      <AccountHeader title="Sign up today!" showCustomerEmail={false} />
    </div>
    <form method="post" onSubmit={onSignup}>
      <p>Get on our exclusive email list and be the first to hear about sales, coupons, new arrivals and more! Don't miss out on the deals. </p>
      <p>
        {siteName} values your privacy. We do not rent, share or sell your personal information. Ever. View our{' '}
        <a href={privacyNotice}>{privacyNoticeText}</a>.
      </p>

      <fieldset className={cn({ [css.fieldError]: errors.includes('email') })}>
        <label htmlFor="recipientEmailValid">Your Email Address</label>
        <input id="recipientEmailValid" name="recipientEmail" required />
        <div>{errors.includes('email') && 'Please enter a valid email.'}</div>
      </fieldset>

      <h3>Are You a Robot?</h3>
      <img src={captcha.url} height={captcha.height} width={captcha.width} alt="Captcha" />
      <fieldset className={cn({ [css.fieldError]: errors.includes('captcha') })}>
        <label htmlFor="captchaAnswer">Please type the word you see above</label>
        <input id="captchaAnswer" type="text" name="answer" required />
        <input type="hidden" value={captcha.token} name="token" />
        <div>{errors.includes('captcha') && 'Please verify you are not a robot using the captcha.'}</div>
      </fieldset>
      <div className={css.buttons}>
        <button type="submit" className={css.saveButton} disabled={false}>
          Sign Up
        </button>
      </div>
    </form>
  </div>
);

const makeSubscriptionsTable = ({
  subscriptionsInfo,
  onBrandChange,
  onListChange,
  onStockChange,
  onUpcomingStyleChange,
  siteName,
  testId,
  isLaunchCalendarHydraAssigned
}) => {
  if (subscriptionsInfo.optout) {
    return (
      <p className="optOutMessage">
        Please confirm that you would no longer like to receive this type of marketing email from {siteName} in the future.
      </p>
    );
  }
  const {
    subscriptions: { emailLists, brandSubscriptions, stockSubscriptions, upcomingStyleSubscriptions }
  } = subscriptionsInfo;

  const makeBrandItems = brandSubscriptions => {
    let tbody;
    if (!brandSubscriptions) {
      return;
    } else if (!brandSubscriptions.length) {
      tbody = (
        <tbody>
          <tr>
            <td data-test-id={testId('noBrandSubs')}>You have no brand subscriptions :(</td>
          </tr>
        </tbody>
      );
    } else {
      tbody = (
        <tbody>
          {brandSubscriptions.map(brand => {
            const { id, name, subscribed } = brand;
            return <BrandSubscriptionListItem key={id} id={id} name={name} subscribed={subscribed} onBrandChange={onBrandChange} />;
          })}
        </tbody>
      );
    }
    return (
      <table className={css.subscriptionsTable}>
        <thead>
          <tr>
            <th>Brand, Initiative, and Campaign Notifications</th>
          </tr>
        </thead>
        {tbody}
      </table>
    );
  };

  const makeEmailListItems = emailLists => {
    if (!emailLists.length) {
      return (
        <tbody>
          <tr>
            <td>You have no email list subscriptions :(</td>
          </tr>
        </tbody>
      );
    } else {
      return (
        <tbody>
          {emailLists.map(emailList => {
            const { emailListName, subscribed } = emailList;
            return (
              <EmailListSubscriptionListItem key={emailListName} onListChange={onListChange} subscribed={subscribed} emailListName={emailListName} />
            );
          })}
        </tbody>
      );
    }
  };

  const makeStockItems = stockSubscriptions => {
    let tbody;
    if (!stockSubscriptions) {
      return;
    } else if (!stockSubscriptions.length) {
      tbody = (
        <tbody>
          <tr>
            <td data-test-id={testId('noStockSubs')}>You have no stock subscriptions :(</td>
          </tr>
        </tbody>
      );
    } else {
      tbody = (
        <tbody>
          {stockSubscriptions.map(product => {
            const { productName, asin, brand, color, size, width, imageId, subscribed } = product;
            return (
              <ProductSubscriptionListItem
                key={asin}
                productName={productName}
                asin={asin}
                brand={brand}
                color={color}
                size={size}
                width={width}
                imageId={imageId}
                subscribed={subscribed}
                onChange={onStockChange}
              />
            );
          })}
        </tbody>
      );
    }
    return (
      <table className={css.subscriptionsTable}>
        <thead>
          <tr>
            <th>Stock Notifications</th>
          </tr>
        </thead>
        {tbody}
      </table>
    );
  };

  const makeUpcomingStyles = upcomingStyleSubscriptions => {
    if (!upcomingStyleSubscriptions) {
      return;
    }

    let body;
    if (!upcomingStyleSubscriptions.length) {
      body = (
        <tr>
          <td data-test-id={testId('noUpcomingStyleSubs')}>You have no upcoming styles subscriptions :(</td>
        </tr>
      );
    } else {
      body = upcomingStyleSubscriptions.map(product => {
        const { productName, brand, color, imageId, subscribed, styleId, productUrl } = product;
        return (
          <ProductSubscriptionListItem
            key={styleId}
            productName={productName}
            brand={brand}
            color={color}
            imageId={imageId}
            subscribed={subscribed}
            onChange={onUpcomingStyleChange}
            styleId={styleId}
            productUrl={productUrl}
          />
        );
      });
    }

    return (
      <>
        <table className={css.subscriptionsTable}>
          <thead>
            <tr>
              <th>Upcoming Styles Notifications</th>
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </>
    );
  };

  return (
    <div className={css.subscriptionsContainer}>
      {
        <div>
          <table className={css.subscriptionsTable}>
            <thead>
              <tr>
                <th>Marketing Messages</th>
              </tr>
            </thead>
            {makeEmailListItems(emailLists)}
          </table>
          {makeBrandItems(brandSubscriptions)}
          {makeStockItems(stockSubscriptions)}
          {isLaunchCalendarHydraAssigned && makeUpcomingStyles(upcomingStyleSubscriptions)}
        </div>
      }
    </div>
  );
};

export const Subscriptions = ({
  subscriptionsInfo,
  toggleSubscriptionBrand,
  toggleSubscriptionList,
  toggleSubscriptionStock,
  toggleSubscriptionUpcomingStyle,
  updateSubscriptions,
  signupForMailingList,
  redirectToAuthenticationFor,
  redirectTo,
  fetchSubscriptionsInfo,
  fetchSubscriptionsSignupCaptcha,
  checkCustomerAuthentication,
  triggerAssignment,
  isCustomer,
  routeParams: requestSpecs,
  location,
  isLaunchCalendarHydraAssigned
}) => {
  const queryStringAuthToken = new URLSearchParams(location.search).get('auth_token');
  const { isLoaded, captcha, successfulSignup, errors } = subscriptionsInfo || {};
  const {
    testId,
    marketplace: {
      api: {
        account: {
          subscriptions: { myAccountIsSubscriptionMaster = false, newsletterSignupInFooter = false }
        }
      },
      checkout: {
        helpUrl: { privacyNotice, privacyNoticeText }
      },
      name: siteName,
      shortName: siteNameShort,
      account: { emailSubscriptionUrl }
    }
  } = useMartyContext();
  const requestType = determineRequestType(requestSpecs, myAccountIsSubscriptionMaster);

  useEffect(() => {
    const emailAuthTokenPresent = !!queryStringAuthToken;

    switch (requestType) {
      case REQUEST_TYPE_SIGNUP:
        if (isCustomer) {
          redirectTo(emailSubscriptionUrl);
        } else {
          fetchSubscriptionsSignupCaptcha(!newsletterSignupInFooter);
        }
        break;
      case REQUEST_TYPE_SUCCESS_MESSAGE:
        redirectTo(emailSubscriptionUrl);
        break;
      case REQUEST_TYPE_MYACCOUNT_AUTH:
        checkCustomerAuthentication(
          emailSubscriptionUrl,
          () => {
            fetchSubscriptionsInfo({ requestSpecs, requestType });
          },
          { redirectOnClose: '/' }
        );
        break;
      case REQUEST_TYPE_VILLAGEIDIOT:
        if (!emailAuthTokenPresent && !isCustomer) {
          redirectToAuthenticationFor(location, '/');
        } else {
          fetchSubscriptionsInfo({ requestSpecs, requestType });
        }
        break;
      case REQUEST_TYPE_MYACCOUNT_TOKEN:
        fetchSubscriptionsInfo({ requestSpecs, requestType });
        break;
    }
  }, [
    checkCustomerAuthentication,
    emailSubscriptionUrl,
    fetchSubscriptionsInfo,
    fetchSubscriptionsSignupCaptcha,
    isCustomer,
    newsletterSignupInFooter,
    queryStringAuthToken,
    redirectTo,
    redirectToAuthenticationFor,
    requestSpecs,
    requestType
  ]);

  useEffectOnce(() => {
    titaniteView();
    triggerAssignment(HYDRA_LAUNCH_CALENDAR);
  });

  useEffect(() => {
    if (!subscriptionsInfo || !subscriptionsInfo.subscriptions) {
      return;
    }
    const {
      subscriptions: { emailLists = [], brandSubscriptions = [], stockSubscriptions = [] }
    } = subscriptionsInfo;
    const hasMarketingSubscription = emailLists.some(marketing => marketing.subscribed);
    const hasBrandSubscription = brandSubscriptions.some(brand => brand.subscribed);
    const hasStockSubscription = stockSubscriptions.some(stock => stock.subscribed);

    track(() => [
      pvSubscriptionsPageView,
      {
        marketing: hasMarketingSubscription,
        brandInitiativeCampaign: hasBrandSubscription,
        stock: hasStockSubscription
      }
    ]);
  }, [subscriptionsInfo]);

  const onBrandChange = ({ target }) => {
    toggleSubscriptionBrand(target.value);

    track(() => [
      evSubscriptionsPageChange,
      {
        emailListName: target.name,
        category: 'BRAND'
      }
    ]);
  };

  const onListChange = ({ target }) => {
    toggleSubscriptionList(target.value);

    track(() => [
      evSubscriptionsPageChange,
      {
        emailListName: target.value,
        category: 'MARKETING'
      }
    ]);
  };

  const onSaveChanges = e => {
    updateSubscriptions(subscriptionsInfo, false, {
      requestSpecs,
      requestType
    });
    const saveBtnEl = e.target.innerHTML;

    track(() => [evSaveChangesClick, { saveChangesBtn: saveBtnEl }]);
  };

  const onSignup = e => {
    const { recipientEmail, answer, token } = e.currentTarget;
    e.preventDefault();
    signupForMailingList(recipientEmail.value, answer.value, token.value);
  };

  const onStockChange = ({ target }) => {
    toggleSubscriptionStock(target.value);

    track(() => [
      evSubscriptionsPageChange,
      {
        emailListName: target.name,
        category: 'STOCK'
      }
    ]);
  };

  const onUpcomingStyleChange = ({ target }) => {
    toggleSubscriptionUpcomingStyle(target.value);

    track(() => [
      evSubscriptionsPageChange,
      {
        emailListName: target.name,
        category: 'UPCOMING_STYLE'
      }
    ]);
  };

  const onUnsubscribeFromAll = e => {
    if (subscriptionsInfo.optout) {
      updateSubscriptions(subscriptionsInfo, true, {
        requestSpecs,
        requestType
      });
    } else {
      const subscriptionsDelta = {};
      Object.keys(subscriptionsInfo.subscriptions).forEach(category => {
        // Include everything here, even if it's not checked b/c unsubscribe from all should not allow for possibility
        // a list was subscribed in another window
        subscriptionsDelta[category] = subscriptionsInfo.subscriptions[category].map(list => ((list.subscribed = false), list));
      });
      updateSubscriptions({ subscriptionsDelta }, true, {
        requestSpecs,
        requestType
      });
      const unsubscribeBtnEl = e.target.innerHTML;
      track(() => [evUnsubscribeFromAllClick, { unsubscribeFromAllBtn: unsubscribeBtnEl }]);
    }
  };

  if (!isLoaded) {
    return <PageLoader centered={true} />;
  }
  if (requestType === REQUEST_TYPE_SIGNUP) {
    if (!newsletterSignupInFooter) {
      return (
        <div className={css.subscriptions}>
          {successfulSignup
            ? makeSuccessBanner(`Thank You! You are now signed up to receive offers and specials from ${siteName}.`)
            : makeSignupForm(captcha, onSignup, {
                siteName,
                privacyNotice,
                privacyNoticeText,
                errors
              })}
        </div>
      );
    } else {
      return '';
    }
  }
  return (
    <SiteAwareMetadata>
      <div className={css.subscriptions}>
        {makeAccountHeader()}
        {successfulSignup && makeSuccessBanner('Your subscription preferences have been updated.')}
        {makeSubscriptionsTable({
          subscriptionsInfo,
          onBrandChange,
          onListChange,
          onStockChange,
          onUpcomingStyleChange,
          siteName,
          testId,
          isLaunchCalendarHydraAssigned
        })}
        <div className={css.buttons}>
          <button type="button" className={css.saveButton} disabled={!isLoaded} onClick={onSaveChanges} data-test-id={testId('saveButton')}>
            {subscriptionsInfo.optout ? 'Unsubscribe' : 'Save Changes'}
          </button>
          <button
            type="button"
            className={css.unsubscribeButton}
            disabled={!isLoaded}
            onClick={onUnsubscribeFromAll}
            data-test-id={testId('unsubscribeButton')}
          >
            Unsubscribe From All Mailings
          </button>
          <p>
            Click the 'Unsubscribe From All Mailings' button to stop receiving all {siteNameShort} marketing communications (except transactional and
            account related emails).
          </p>
        </div>
      </div>
    </SiteAwareMetadata>
  );
};

export function mapStateToProps(state) {
  return {
    isCustomer: !!state.cookies['x-main'],
    account: state.account,
    subscriptionsInfo: state.subscriptionsInfo,
    isLaunchCalendarHydraAssigned: isAssigned(HYDRA_LAUNCH_CALENDAR, 1, state)
  };
}

export default connect(mapStateToProps, {
  checkCustomerAuthentication,
  fetchSubscriptionsInfo,
  fetchSubscriptionsSignupCaptcha,
  updateSubscriptions,
  toggleSubscriptionList,
  toggleSubscriptionStock,
  toggleSubscriptionUpcomingStyle,
  toggleSubscriptionBrand,
  redirectToAuthenticationFor,
  redirectTo,
  signupForMailingList,
  triggerAssignment
})(Subscriptions);
